<template>
  <section
    class="complaint-record"
    :class="{
      'complaint-record--chart-only': chartOnly && !showEmptyState,
      'complaint-record--compact-chart-only':
        chartOnly && compactChartOnly && !showEmptyState,
      'complaint-record--empty': showEmptyState,
      'complaint-record--chart-open':
        showChart &&
        !complaintDashboardLoading &&
        !complaintDashboardError &&
        !showEmptyState,
    }"
  >
    <div
      v-if="complaintDashboardLoading"
      class="complaint-record-loading"
    >
      <f7-preloader></f7-preloader>
      <span>{{ $t('loading') }}</span>
    </div>

    <template v-else-if="complaintDashboardError">
      <div class="complaint-record-header">
        <div class="complaint-record-title">
          <span class="complaint-record-title-icon">
            <img :src="complaintIcon" alt="" />
          </span>
          <h2>{{ widgetTitle }}</h2>
        </div>
        <button
          v-if="canRegisterComplaints"
          type="button"
          class="complaint-record-primary-button"
          @click="goToComplaintRegister"
        >
          {{ buttonRegisterText }}
        </button>
      </div>

      <div class="complaint-record-error" role="status">
        {{ errorMessage }}
      </div>
    </template>

    <template v-else-if="showEmptyState">
      <div class="complaint-record-header">
        <div class="complaint-record-title">
          <span class="complaint-record-title-icon">
            <img :src="complaintIcon" alt="" />
          </span>
          <h2>{{ widgetTitle }}</h2>
        </div>

        <button
          v-if="canRegisterComplaints"
          type="button"
          class="complaint-record-primary-button"
          @click="goToComplaintRegister"
        >
          {{ buttonRegisterText }}
        </button>
      </div>

      <div class="complaint-record-empty-body">
        <div
          class="complaint-record-empty-illustration"
          aria-hidden="true"
        >
          <div class="complaint-record-empty-icon">
            <img
              :src="emptyIllustrationSrc"
              alt=""
              @error="handleEmptyIllustrationError"
            />
          </div>
        </div>

        <div class="complaint-record-empty-content">
          <h2 id="complaint-record-emptytitle">{{ emptyTitle }}</h2>
          <p class="complaint-record-empty-main">
            {{ emptyMainMessage }}
          </p>
          <p>{{ emptySecondaryMessage }}</p>
          <p>{{ emptyHelpMessage }}</p>
        </div>
      </div>
    </template>

    <template v-else-if="chartOnly">
      <aside
        class="complaint-record-chart-panel"
        :aria-label="$t('complaintDashboard.chartSummary')"
      >
        <div class="complaint-record-chart-only-header">
          <div class="complaint-record-title">
            <span class="complaint-record-title-icon">
              <img :src="complaintIcon" alt="" />
            </span>
            <h2>{{ widgetTitle }}</h2>
          </div>

          <button
            v-if="canRegisterComplaints"
            type="button"
            class="complaint-record-primary-button"
            @click="goToComplaintRegister"
          >
            {{ buttonRegisterText }}
          </button>
        </div>

        <div class="complaint-record-chart-panel-inner">
          <div
            class="complaint-record-chart-content"
            :class="{
              'complaint-record-chart-content--empty': !chartTotal,
            }"
          >
            <div class="complaint-record-chart-visual">
              <svg
                class="complaint-record-donut"
                viewBox="0 0 120 120"
                role="img"
                :aria-label="chartAriaLabel"
              >
                <circle
                  class="complaint-record-donut-track"
                  cx="60"
                  cy="60"
                  r="44"
                ></circle>
                <circle
                  v-for="segment in chartSegments"
                  :key="segment.key"
                  class="complaint-record-donut-segment"
                  :class="{
                    'complaint-record-donut-segment--active':
                      activeChartSegment &&
                      activeChartSegment.key === segment.key,
                  }"
                  cx="60"
                  cy="60"
                  r="44"
                  :stroke="segment.color"
                  :style="{
                    strokeDasharray: segment.dasharray,
                    strokeDashoffset: segment.dashoffset,
                  }"
                  tabindex="0"
                  role="button"
                  :aria-label="segment.tooltip"
                  @mouseenter="setActiveChartSegment(segment)"
                  @mouseleave="clearActiveChartSegment"
                  @focus="setActiveChartSegment(segment)"
                  @blur="clearActiveChartSegment"
                  @click="goToComplaintStatus(segment.estado)"
                  @keyup.enter="goToComplaintStatus(segment.estado)"
                  @keyup.space.prevent="
                    goToComplaintStatus(segment.estado)
                  "
                ></circle>
              </svg>

              <div class="complaint-record-donut-center">
                <strong>{{ chartTotal }}</strong>
                <span>{{ $t('complaintDashboard.incidents') }}</span>
              </div>
            </div>

            <ul class="complaint-record-chart-legend">
              <li
                v-for="item in chartLegendItems"
                :key="item.key"
                class="complaint-record-chart-legend-item"
                :style="{
                  '--complaint-chart-active-color': item.color,
                }"
                :class="{
                  'complaint-record-chart-legend-item--active':
                    activeChartSegment &&
                    activeChartSegment.key === item.key,
                }"
              >
                <button
                  type="button"
                  @mouseenter="setActiveChartSegment(item)"
                  @mouseleave="clearActiveChartSegment"
                  @focus="setActiveChartSegment(item)"
                  @blur="clearActiveChartSegment"
                  @click="goToComplaintStatus(item.estado)"
                >
                  <span
                    class="complaint-record-chart-legend-dot"
                    :style="{ backgroundColor: item.color }"
                  ></span>
                  <span class="complaint-record-chart-legend-label">
                    {{ item.label }}
                  </span>
                  <strong>{{ item.count }}</strong>
                </button>
              </li>
            </ul>
          </div>

          <p v-if="!chartTotal" class="complaint-record-chart-empty">
            {{ $t('complaintDashboard.chartEmpty') }}
          </p>
        </div>
      </aside>
    </template>

    <template v-else>
      <div class="complaint-record-layout">
        <div class="complaint-record-main">
          <div class="complaint-record-header">
            <div class="complaint-record-title">
              <span class="complaint-record-title-icon">
                <img :src="complaintIcon" alt="" />
              </span>
              <h2>{{ widgetTitle }}</h2>
            </div>
            <div class="complaint-record-actions">
              <button
                v-if="canRegisterComplaints"
                type="button"
                class="complaint-record-primary-button"
                @click="goToComplaintRegister"
              >
                {{ buttonRegisterText }}
              </button>
              <button
                type="button"
                class="complaint-record-chart-toggle"
                :class="{
                  'complaint-record-chart-toggle--active': showChart,
                }"
                :aria-label="chartToggleAriaLabel"
                :aria-pressed="showChart ? 'true' : 'false'"
                @click="toggleChart"
              >
                <i class="f7-icons" aria-hidden="true">
                  {{ chartToggleIcon }}
                </i>
              </button>
            </div>
          </div>

          <div class="complaint-record-cards">
            <button
              v-for="card in dashboardCards"
              :key="card.key"
              type="button"
              class="complaint-record-card"
              :class="`complaint-record-card--${card.variant}`"
              @click="goToComplaintStatus(card.estado)"
            >
              <div class="complaint-record-card-accent"></div>
              <span class="complaint-record-card-icon">
                <img :src="card.icon" alt="" />
              </span>
              <div class="complaint-record-card-content">
                <h4 class="complaint-record-card-count">
                  {{ card.contador }}
                </h4>
                <p class="complaint-record-card-description">
                  {{ card.titulo }}
                </p>
              </div>
              <span
                class="complaint-record-card-arrow f7-icons"
                aria-hidden="true"
              >
                chevron_right
              </span>
            </button>
          </div>
        </div>

        <transition name="complaint-record-chart">
          <aside
            v-if="showChart"
            class="complaint-record-chart-panel"
            :aria-label="$t('complaintDashboard.chartSummary')"
          >
            <div class="complaint-record-chart-panel-inner">
              <div
                class="complaint-record-chart-content"
                :class="{
                  'complaint-record-chart-content--empty':
                    !chartTotal,
                }"
              >
                <div class="complaint-record-chart-visual">
                  <svg
                    class="complaint-record-donut"
                    viewBox="0 0 120 120"
                    role="img"
                    :aria-label="chartAriaLabel"
                  >
                    <circle
                      class="complaint-record-donut-track"
                      cx="60"
                      cy="60"
                      r="44"
                    ></circle>
                    <circle
                      v-for="segment in chartSegments"
                      :key="segment.key"
                      class="complaint-record-donut-segment"
                      :class="{
                        'complaint-record-donut-segment--active':
                          activeChartSegment &&
                          activeChartSegment.key === segment.key,
                      }"
                      cx="60"
                      cy="60"
                      r="44"
                      :stroke="segment.color"
                      :style="{
                        strokeDasharray: segment.dasharray,
                        strokeDashoffset: segment.dashoffset,
                      }"
                      tabindex="0"
                      role="button"
                      :aria-label="segment.tooltip"
                      @mouseenter="setActiveChartSegment(segment)"
                      @mouseleave="clearActiveChartSegment"
                      @focus="setActiveChartSegment(segment)"
                      @blur="clearActiveChartSegment"
                      @click="goToComplaintStatus(segment.estado)"
                      @keyup.enter="
                        goToComplaintStatus(segment.estado)
                      "
                      @keyup.space.prevent="
                        goToComplaintStatus(segment.estado)
                      "
                    ></circle>
                  </svg>

                  <div class="complaint-record-donut-center">
                    <strong>{{ chartTotal }}</strong>
                    <span>{{
                      $t('complaintDashboard.incidents')
                    }}</span>
                  </div>
                </div>

                <ul class="complaint-record-chart-legend">
                  <li
                    v-for="item in chartLegendItems"
                    :key="item.key"
                    class="complaint-record-chart-legend-item"
                    :style="{
                      '--complaint-chart-active-color': item.color,
                    }"
                    :class="{
                      'complaint-record-chart-legend-item--active':
                        activeChartSegment &&
                        activeChartSegment.key === item.key,
                    }"
                  >
                    <button
                      type="button"
                      @mouseenter="setActiveChartSegment(item)"
                      @mouseleave="clearActiveChartSegment"
                      @focus="setActiveChartSegment(item)"
                      @blur="clearActiveChartSegment"
                      @click="goToComplaintStatus(item.estado)"
                    >
                      <span
                        class="complaint-record-chart-legend-dot"
                        :style="{ backgroundColor: item.color }"
                      ></span>
                      <span
                        class="complaint-record-chart-legend-label"
                      >
                        {{ item.label }}
                      </span>
                      <strong>{{ item.count }}</strong>
                    </button>
                  </li>
                </ul>
              </div>

              <p
                v-if="!chartTotal"
                class="complaint-record-chart-empty"
              >
                {{ $t('complaintDashboard.chartEmpty') }}
              </p>
            </div>
          </aside>
        </transition>
      </div>
    </template>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import { hasComplaintRegisterAccess } from '@/js/routeAccess';
import complaintIcon from '@/static/icons/or-ic-incidencia.svg';
import pendingIcon from '@/static/icons/clock-circular-outline.svg';
import inProgressIcon from '@/static/icons/en_progreso.svg';
import resolvedIcon from '@/static/icons/check.svg';
import emptyIconFallback from '@/static/icons/ic_success_alert.svg';

const CARD_ORDER = ['pendingAction', 'inProgress', 'resolved'];
const DONUT_CIRCUMFERENCE = 276.46;

const CHART_COLORS = {
  pending: '#f27a00',
  progress: '#0f8bdc',
  resolved: '#48a942',
  rejected: '#aebacc',
};

const CARD_FALLBACKS = {
  pendingAction: {
    key: 'pendingAction',
    titleKey: 'complaintDashboard.cards.pendingAction',
    contador: 0,
    estado: 'pending_action',
    variant: 'pending',
    icon: pendingIcon,
  },
  inProgress: {
    key: 'inProgress',
    titleKey: 'complaintDashboard.cards.inProgress',
    contador: 0,
    estado: 'in_progress',
    variant: 'progress',
    icon: inProgressIcon,
  },
  resolved: {
    key: 'resolved',
    titleKey: 'complaintDashboard.cards.resolved',
    contador: 0,
    estado: 'resolved',
    variant: 'resolved',
    icon: resolvedIcon,
  },
};

const TRANSLATED_FALLBACKS = {
  title: {
    key: 'complaintDashboard.title',
    values: ['Mis incidencias'],
  },
  register: {
    key: 'complaintDashboard.register',
    values: ['Registrar incidencia'],
  },
  emptyTitle: {
    key: 'complaintDashboard.emptyTitle',
    values: ['Todo en orden'],
  },
  emptyMain: {
    key: 'complaintDashboard.emptyMain',
    values: ['No has reportado ningún problema con nosotros.'],
  },
  emptySecondary: {
    key: 'complaintDashboard.emptySecondary',
    values: ['Gracias por ser parte de nuestra comunidad.'],
  },
  emptyHelp: {
    key: 'complaintDashboard.emptyHelp',
    values: ['Seguimos aquí si alguna vez necesitas ayuda.'],
  },
};

function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export default {
  name: 'ComplaintRecord',
  props: {
    autoLoad: {
      type: Boolean,
      default: true,
    },
    chartOnly: {
      type: Boolean,
      default: false,
    },
    compactChartOnly: {
      type: Boolean,
      default: false,
    },
    canRegisterComplaint: {
      type: Boolean,
      default: null,
    },
  },
  data() {
    return {
      showChart: this.chartOnly,
      emptyImageHasError: false,
      activeChartSegment: null,
    };
  },
  computed: {
    complaintIcon: () => complaintIcon,
    emptyIllustrationSrc() {
      return this.emptyImageHasError
        ? emptyIconFallback
        : './static/icons/complaint-empty-positive.png';
    },
    widgetTitle() {
      return this.getTranslatedFallback(
        this.complaintDashboard?.titulo,
        'title'
      );
    },
    buttonRegisterText() {
      return this.getTranslatedFallback(
        this.complaintDashboard?.textoBotonAlta,
        'register'
      );
    },
    canRegisterComplaints() {
      if (this.canRegisterComplaint !== null) {
        return this.canRegisterComplaint;
      }

      return hasComplaintRegisterAccess({
        enabled: this.enabled,
        authenticatedUser: this.authenticatedUser,
        company: this.company,
      });
    },
    dashboardTotal() {
      return Number(this.complaintDashboard?.total || 0);
    },
    unresolvedDashboardTotal() {
      const pending = this.getDashboardCardCount('pendingAction');
      const inProgress = this.getDashboardCardCount('inProgress');

      return pending + inProgress;
    },
    showAllResolvedState() {
      return (
        !!this.complaintDashboard &&
        this.dashboardTotal > 0 &&
        this.unresolvedDashboardTotal === 0
      );
    },
    showNeverCreatedState() {
      return !!this.complaintDashboard && this.dashboardTotal === 0;
    },
    showEmptyState() {
      return this.showNeverCreatedState || this.showAllResolvedState;
    },
    emptyTitle() {
      return this.getTranslatedFallback(
        this.complaintDashboard?.mensajeVacioTitulo,
        'emptyTitle'
      );
    },
    emptyMainMessage() {
      if (this.showAllResolvedState) {
        return this.$t('complaintDashboard.emptyMain');
      }

      return this.getTranslatedFallback(
        this.complaintDashboard?.mensajeVacioPrincipal,
        'emptyMain'
      );
    },
    emptySecondaryMessage() {
      return this.getTranslatedFallback(
        this.complaintDashboard?.mensajeVacioSecundario,
        'emptySecondary'
      );
    },
    emptyHelpMessage() {
      return this.getTranslatedFallback(
        this.complaintDashboard?.mensajeVacioAyuda,
        'emptyHelp'
      );
    },
    errorMessage() {
      return this.$t('complaintDashboard.loadingError');
    },
    dashboardCards() {
      const cards = Array.isArray(this.complaintDashboard?.tarjetas)
        ? this.complaintDashboard.tarjetas
        : [];
      const cardsByKey = cards.reduce((acc, card) => {
        if (CARD_ORDER.includes(card?.key)) {
          acc[card.key] = card;
        }
        return acc;
      }, {});

      const resolvedCards = CARD_ORDER.map((key) => {
        const fallback = CARD_FALLBACKS[key];
        const card = cardsByKey[key] || {};

        return {
          ...fallback,
          ...card,
          titulo: this.getTranslatedCardTitle(card.titulo, key),
          descripcion:
            card.descripcion ||
            this.$t('complaintDashboard.incidents'),
        };
      });
      return resolvedCards;
    },
    rejectedChartCard() {
      const cards = Array.isArray(this.complaintDashboard?.tarjetas)
        ? this.complaintDashboard.tarjetas
        : [];

      return cards.find((card) => {
        const cardKey = String(card?.key || '').toLowerCase();
        const cardStatus = String(card?.estado || '').toLowerCase();
        const cardVariant = String(card?.variant || '').toLowerCase();

        return [cardKey, cardStatus, cardVariant].some((value) =>
          [
            'rejected',
            'rejected_action',
            'rechazada',
            'rechazadas',
          ].includes(value)
        );
      });
    },
    chartLegendItems() {
      const baseItems = this.dashboardCards.map((card) => ({
        key: card.key,
        label: card.titulo,
        count: Number(card.contador) || 0,
        estado: card.estado,
        color: CHART_COLORS[card.variant] || CHART_COLORS.pending,
      }));

      if (this.rejectedChartCard) {
        baseItems.push({
          key: this.rejectedChartCard.key || 'rejected',
          label:
            this.rejectedChartCard.titulo ||
            this.$t('complaintDashboard.cards.rejected'),
          count: Number(this.rejectedChartCard.contador) || 0,
          estado: this.rejectedChartCard.estado || 'rejected',
          color: CHART_COLORS.rejected,
        });
      }

      return baseItems;
    },
    chartTotal() {
      return this.chartLegendItems.reduce(
        (total, item) => total + item.count,
        0
      );
    },
    chartSegments() {
      if (!this.chartTotal) {
        return [];
      }

      let accumulatedLength = 0;

      return this.chartLegendItems
        .filter((item) => item.count > 0)
        .map((item) => {
          const length =
            (item.count / this.chartTotal) * DONUT_CIRCUMFERENCE;
          const segment = {
            ...item,
            dasharray: `${length} ${DONUT_CIRCUMFERENCE - length}`,
            dashoffset: `${-accumulatedLength}`,
            tooltip: this.$t('complaintDashboard.chartTooltip', {
              count: item.count,
              label: item.label,
            }),
          };

          accumulatedLength += length;
          return segment;
        });
    },
    chartToggleIcon() {
      return this.showChart ? 'multiply' : 'chart_pie';
    },
    chartToggleAriaLabel() {
      return this.showChart
        ? this.$t('complaintDashboard.chartToggleClose')
        : this.$t('complaintDashboard.chartToggleOpen');
    },
    chartAriaLabel() {
      if (!this.chartTotal) {
        return this.$t('complaintDashboard.chartWithoutData');
      }

      return this.$t('complaintDashboard.chartWithTotal', {
        total: this.chartTotal,
      });
    },
    ...mapState('contact', ['company', 'enabled']),
    ...mapState('user', ['authenticatedUser']),
    ...mapState('complaintManagement', [
      'complaintDashboard',
      'complaintDashboardLoading',
      'complaintDashboardError',
    ]),
  },
  watch: {
    company() {
      if (this.autoLoad) {
        this.loadComplaintDashboard();
      }
    },
  },
  mounted() {
    if (this.autoLoad) {
      this.loadComplaintDashboard();
    }
  },
  methods: {
    getTranslatedFallback(value, fallbackName) {
      const fallback = TRANSLATED_FALLBACKS[fallbackName];

      if (!fallback) {
        return value || '';
      }

      const isKnownFallback = fallback.values
        .map((item) => normalizeText(item))
        .includes(normalizeText(value));

      return value && !isKnownFallback
        ? value
        : this.$t(fallback.key);
    },
    getTranslatedCardTitle(value, cardKey) {
      const fallback = CARD_FALLBACKS[cardKey];

      if (!fallback) {
        return value || '';
      }

      const knownFallbacks = {
        pendingAction: [
          'Pendientes de gestión',
          'Pendientes de gestion',
        ],
        inProgress: ['En gestión', 'En gestion'],
        resolved: ['Resueltas'],
      };
      const isKnownFallback = (knownFallbacks[cardKey] || [])
        .map((item) => normalizeText(item))
        .includes(normalizeText(value));

      return value && !isKnownFallback
        ? value
        : this.$t(fallback.titleKey);
    },
    getDashboardCardCount(key) {
      const card = this.dashboardCards.find(
        (item) => item.key === key
      );

      return Number(card?.contador || 0);
    },
    goToComplaintStatus(status) {
      this.$f7router.navigate(
        `/ComplaintManagement/?estado=${encodeURIComponent(status)}`,
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    goToComplaintRegister() {
      if (!this.canRegisterComplaints) {
        return;
      }

      this.$f7router.navigate('/complaintRegister', {
        animate: true,
        transition: 'f7-fade',
      });
    },
    toggleChart() {
      this.showChart = !this.showChart;

      if (!this.showChart) {
        this.clearActiveChartSegment();
      }
    },
    setActiveChartSegment(segment) {
      this.activeChartSegment = segment;
    },
    clearActiveChartSegment() {
      this.activeChartSegment = null;
    },
    handleEmptyIllustrationError() {
      this.emptyImageHasError = true;
    },
  },
};
</script>

<style scoped lang="scss">
@import './style.scss';
</style>
